
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";

/**
 * Hook pour suivre l'utilisation des outils
 */
export const useToolUsageTracker = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  /**
   * Enregistre l'utilisation d'un outil
   * @param toolName Nom de l'outil utilisé
   */
  const trackToolUsage = useCallback(async (toolName: string) => {
    if (!user) {
      // Si l'utilisateur n'est pas connecté, on ne fait rien sans erreur ni toast
      console.log("Aucun utilisateur connecté, utilisation non enregistrée");
      return;
    }

    try {
      console.log(`Vérification et enregistrement de l'utilisation de l'outil: ${toolName}`);
      
      // Vérifier si l'utilisateur a déjà utilisé cet outil dans les dernières 24 heures
      const oneDayAgo = new Date();
      oneDayAgo.setHours(oneDayAgo.getHours() - 24);
      
      const { data: recentUsage, error: recentUsageError } = await supabase
        .from("tool_usage")
        .select("id")
        .eq("user_id", user.id)
        .eq("tool_name", toolName)
        .gte("created_at", oneDayAgo.toISOString())
        .limit(1);
        
      if (recentUsageError) {
        console.error("Erreur lors de la vérification de l'utilisation récente:", recentUsageError);
        // On continue malgré cette erreur
      } else if (recentUsage && recentUsage.length > 0) {
        console.log(`Utilisation récente trouvée pour ${toolName}, pas d'enregistrement supplémentaire nécessaire`);
        return; // Sortir si une utilisation récente a été trouvée
      }
      
      // 1. Récupérer l'ID de l'outil et sa catégorie
      const { data: toolData, error: toolError } = await supabase
        .from("tools")
        .select("id, category_id")
        .eq("name", toolName)
        .maybeSingle();

      if (toolError) {
        console.error(`Erreur lors de la récupération de l'outil ${toolName}:`, toolError);
        // Gérer l'erreur mais continuer
      }

      // Si l'outil n'est pas trouvé avec une correspondance exacte, essayer avec une recherche insensible à la casse
      if (!toolData) {
        console.log(`Outil non trouvé: ${toolName}, recherche alternative...`);
        
        const { data: toolDataAlt, error: toolErrorAlt } = await supabase
          .from("tools")
          .select("id, category_id")
          .ilike("name", `%${toolName}%`)
          .maybeSingle();
          
        if (toolErrorAlt || !toolDataAlt) {
          console.error("Outil non trouvé même avec recherche alternative:", toolErrorAlt || "Aucun résultat");
          
          // Récupérer la catégorie par défaut (Marketing & Business)
          const { data: categoryData } = await supabase
            .from("tool_categories")
            .select("id")
            .eq("name", "Marketing & Business")
            .maybeSingle();
            
          const defaultCategoryId = categoryData?.id || "00000000-0000-0000-0000-000000000000";
          
          // Insérer un nouvel enregistrement avec gestion d'erreur silencieuse
          try {
            const { error: insertError } = await supabase
              .from("tool_usage")
              .insert({
                user_id: user.id,
                tool_name: toolName,
                category_id: defaultCategoryId
              });
              
            if (insertError) {
              // Ne pas afficher d'erreur toast à l'utilisateur
              console.error("Erreur lors de l'insertion:", insertError);
            }
          } catch (error) {
            console.error("Erreur lors de l'insertion (try/catch):", error);
          }
          
          return;
        }
        
        console.log("Outil trouvé via recherche alternative:", toolDataAlt);
        
        // Insérer un nouvel enregistrement avec gestion d'erreur silencieuse
        try {
          const { error: insertError } = await supabase
            .from("tool_usage")
            .insert({
              user_id: user.id,
              tool_id: toolDataAlt.id,
              tool_name: toolName,
              category_id: toolDataAlt.category_id
            });
            
          if (insertError) {
            console.error("Erreur lors de l'insertion de l'utilisation:", insertError);
            // On ne montre pas d'erreur toast à l'utilisateur
          }
        } catch (error) {
          console.error("Erreur lors de l'insertion (try/catch):", error);
        }
        
        return;
      }

      // Insérer un nouvel enregistrement avec gestion d'erreur silencieuse
      try {
        const { error: insertError } = await supabase
          .from("tool_usage")
          .insert({
            user_id: user.id,
            tool_id: toolData.id,
            tool_name: toolName,
            category_id: toolData.category_id
          });
          
        if (insertError) {
          console.error("Erreur lors de l'insertion de l'utilisation:", insertError);
          // On ne montre pas d'erreur toast à l'utilisateur
        } else {
          console.log(`Utilisation de l'outil ${toolName} enregistrée avec succès`);
        }
      } catch (error) {
        console.error("Erreur lors de l'insertion (try/catch):", error);
      }
      
    } catch (error) {
      console.error("Erreur lors du suivi d'utilisation de l'outil:", error);
      // Ne pas afficher de toast pour les erreurs de tracking - expérience utilisateur fluide
    }
  }, [user]);

  return { trackToolUsage };
};
